import { useEffect, useMemo, useRef, useState } from 'react'
import { addOptionLS } from '@/lib/lookups'

type Props = {
  label?: string
  options: string[]
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  storageKey?: string   // if set, newly created options are persisted
  maxChips?: number     // visual cap before "+N more"
}

export default function MultiSelect({ label, options, value, onChange, placeholder, storageKey, maxChips = 6 }: Props){
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const boxRef = useRef<HTMLDivElement | null>(null)

  const filtered = useMemo(()=>{
    const s = q.trim().toLowerCase()
    const pool = options.filter(o => !value.includes(o))
    if (!s) return pool
    return pool.filter(o => o.toLowerCase().includes(s))
  },[options, value, q])

  useEffect(()=>{
    function onDoc(e: MouseEvent){
      if (!boxRef.current) return
      if (!boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDoc)
    return ()=> document.removeEventListener('mousedown', onDoc)
  },[open])

  function add(val: string){
    const v = val.trim()
    if (!v) return
    if (!value.includes(v)){
      onChange([...value, v])
      if (storageKey) addOptionLS(storageKey, v)
    }
    setQ('')
  }
  function remove(idx:number){
    const next = [...value]; next.splice(idx,1); onChange(next)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>){
    if (e.key === 'Enter'){ e.preventDefault(); add(q) }
    if (e.key === 'Backspace' && !q && value.length){ remove(value.length-1) }
  }

  const hiddenCount = Math.max(0, (value?.length || 0) - maxChips)

  return (
    <div className="grid gap-2" ref={boxRef}>
      {label && <label className="text-sm">{label}</label>}
      <div className="border border-[color:var(--border)] rounded-[var(--radius-control)] px-2 py-1.5">
        <div className="flex flex-wrap gap-2">
          {(value || []).slice(0, maxChips).map((v, i)=>(
            <span key={v} className="badge">
              {v}
              <button className="ml-2" onClick={()=>remove(i)} aria-label={`Remove ${v}`}>×</button>
            </span>
          ))}
          {hiddenCount>0 && <span className="badge">+{hiddenCount} more</span>}
          <input
            className="outline-none text-sm flex-1 min-w-[120px]"
            placeholder={placeholder || 'Type to search or add…'}
            value={q}
            onChange={e=>{ setQ(e.target.value); setOpen(true) }}
            onKeyDown={onKeyDown}
            onFocus={()=>setOpen(true)}
          />
        </div>
      </div>

      {open && (
        <div className="popover absolute mt-10 w-full bg-white shadow-xl z-40">
          {filtered.length === 0 ? (
            <div className="p-2 text-sm text-[var(--muted)]">
              No matches. Press <span className="font-medium">Enter</span> to add “{q.trim()}”.
            </div>
          ) : (
            <ul className="max-h-56 overflow-auto">
              {filtered.map(opt=>(
                <li key={opt}>
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-[var(--surface-1)]"
                    onClick={()=> add(opt)}
                  >
                    {opt}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {q.trim() && !options.includes(q.trim()) && (
            <div className="border-t border-[color:var(--border)]">
              <button className="w-full text-left px-3 py-2 hover:bg-[var(--surface-1)]" onClick={()=>add(q)}>
                + Add “{q.trim()}”
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
